/**
 * Calculate the actual position for the jitter plot on the chart
 *
 * @param api The selected series.
 * @param {int} plotIndex The index of the plot in current category.
 * @returns Transformed series styles.
 */
import {
  BLACK_HEX,
  BOX_PLOT_HIGHLIGHTED_Z_INDEX,
  DEFAULT_Z_INDEX
} from "constants/chart.constants";
import { HIGHLIGHTED_Z_INDEX } from "entities/charts/eva/translators/constants";
import seedrandom from "seedrandom";

const NUM_BOXPLOTS_IN_COL = 1;

export function getJitterPlotPosition(api, size: number) {
  const coord = api.coord([api.value(0), api.value(1)]);
  const offset = api.size([1, 0]);
  // The runtime can hand back unusable coord/size values in degraded coordinate
  // system states. A throw here takes the whole chart down through the React
  // error boundary, so skip the datum (render nothing) instead.
  if (!Array.isArray(coord) || !Array.isArray(offset) || !Number.isFinite(offset[0])) {
    return;
  }
  const color = api.value(2);
  const uwi = api.value(3);
  const forecast = api.value(4);
  const pointOpacity = api.value(5);
  const isEmphasized = !!api.value(7);

  const availableWidth = offset[0] * 0.8 - 2;
  const boxGap = (availableWidth / NUM_BOXPLOTS_IN_COL) * 0.3;
  const boxWidth =
    (availableWidth - boxGap * (NUM_BOXPLOTS_IN_COL - 1)) / NUM_BOXPLOTS_IN_COL;
  let boxBase = boxWidth / 2 - availableWidth / 2;

  const rng = new seedrandom(uwi);
  boxBase += (rng() * 2 - 1) * (availableWidth - boxGap * 4);

  coord[0] += boxBase;
  const isFocus = color === BLACK_HEX; //is focus if is black

  return {
    type: "circle",
    shape: {
      cx: coord[0],
      cy: coord[1],
      r: size
    },
    // z2, not z: echarts overwrites a custom series element's `z` with the series-level
    // z and copies through only `z2` (CustomView: `elDisplayable.z = seriesModel.currentZ`).
    // Jitter dots share zlevel 0 and the default series z, so z2 is what sorts them.
    z2: isFocus
      ? BOX_PLOT_HIGHLIGHTED_Z_INDEX
      : isEmphasized
      ? HIGHLIGHTED_Z_INDEX
      : DEFAULT_Z_INDEX,
    style: {
      fill: forecast ? "none" : color,
      stroke: forecast ? color : "none",
      opacity: pointOpacity
    }
  };
}
